import { Checkbox } from "components/Atoms/inputs/Checkbox";
import FreeFieldCategoryDropdown from "components/dropdowns/reactselect/FreeFieldCategoryDropdown";
import { FreeFieldTypeRadio } from "components/Freefields/FreeFieldTypeRadio";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import PercentageInput from "components/PercentageInput";
import { FreeFieldReferenceInput } from "components/ValidatedFields/FreeFieldReferenceInput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React, { useState } from "react";
import { getFormElementType } from "utilities/freefieldUtils";
import { routes } from "utilities/routing/generatedRoutes";
import { FreeFieldDefinitionNewState } from "./model";
import { getOptionalTranslationValueFieldsForNew, getRequiredTranslationValueFieldsForNew, useTranslationValuesForNewState } from "webform/components/TranslationValueInnerForm";
import { DropdownOptionsNew } from "./DropdownOptions/DropdownOptions";

interface FreeFieldDefinitionNewSectionProps {
  initialCategory: server.dto.FreeFieldCategoryForList
  mainArea: server.dto.MainAreas
}

export const FreeFieldDefinitionNewSection = (props: FreeFieldDefinitionNewSectionProps) => {
  const [freeFieldDefinition, setFreeFieldDefinition] = useState<FreeFieldDefinitionNewState>({
    category: props.initialCategory,
    type: getFormElementType("boolean"),
    label: [],
    subLabel: [],
    reference: "",
    hiddenFromItinerary: false,

    dropdownOptions: [],
    defaultNumberWithDecimal: null,
    defaultNumberWithoutDecimal: null,
    defaultText: "",
    defaultPercentage: null,
  });

  const labelState = useTranslationValuesForNewState(freeFieldDefinition.label, (label) => setFreeFieldDefinition((state) => ({ ...state, label })));
  const subLabelState = useTranslationValuesForNewState(freeFieldDefinition.subLabel, (subLabel) => setFreeFieldDefinition((state) => ({ ...state, subLabel })));

  const submit = () => {
    return window.CoreApi.FreeFieldDefinition.Create({
      dropdownOptions: freeFieldDefinition.dropdownOptions,
      defaultDecimal: freeFieldDefinition.defaultNumberWithDecimal,
      defaultInteger: freeFieldDefinition.defaultNumberWithoutDecimal,
      defaultText: freeFieldDefinition.defaultText,
      description: freeFieldDefinition.subLabel,
      formElementType: freeFieldDefinition.type,
      freeFieldCategoryId: freeFieldDefinition.category.freeFieldCategoryId,
      hideFromItinerary: freeFieldDefinition.hiddenFromItinerary,
      reference: freeFieldDefinition.reference,
      title: freeFieldDefinition.label,
      defaultPercentage: freeFieldDefinition.defaultPercentage,
      useUntil: null,
    });
  }

  return <EditSectionTemplate
    submit={submit}
    redirectUrl={(id) => window.location.href = routes.freeFieldDefinition_Detail(id).absolute}
    sections={[{
      key: "freeFieldDefinition",
      type: "regular",
      title: RESX.FreeField.resxFreeField,
      fields: [{
        key: "category",
        label: RESX.FreeField.Category,
        renderEditNew: () => <FreeFieldCategoryDropdown
          value={freeFieldDefinition.category}
          onChange={(category) => setFreeFieldDefinition({ ...freeFieldDefinition, category })}
          mainArea={props.mainArea}
          options={undefined}
        />
      }, {
        key: "type",
        label: RESX.FreeField.FieldType,
        renderEditNew: () => <div className="main-section-template-fields-padding">
          <FreeFieldTypeRadio
            value={freeFieldDefinition.type.value}
            onChange={(type) => setFreeFieldDefinition({ ...freeFieldDefinition, type: getFormElementType(type) })}
          />
        </div>
      }, {
        key: "label",
        label: RESX.GeneralLabels.Label,
        hideLabel: true,
        renderEditNew: () => <div className="main-section-template-fields-padding">
          <h3>{RESX.GeneralLabels.Label}</h3>
        </div>
      },
      ...getRequiredTranslationValueFieldsForNew("label", labelState),
      {
        key: "subLabel",
        label: RESX.GeneralLabels.LabelSubscript,
        hideLabel: true,
        renderEditNew: () => <div className="main-section-template-fields-padding">
          <h3>{RESX.GeneralLabels.LabelSubscript}</h3>
        </div>
      },
      ...getOptionalTranslationValueFieldsForNew("subLabel", subLabelState),
      {
        key: "configuration",
        label: RESX.GeneralLabels.Configuration,
        hideLabel: true,
        renderEditNew: () => <div className="main-section-template-fields-padding">
          <h3>{RESX.GeneralLabels.Configuration}</h3>
        </div>
      }, {
        key: "reference",
        label: RESX.FreeField.ReferenceCode,
        sublabel: RESX.FreeField.ReferenceCodeDescription,
        renderEditNew: () => <FreeFieldReferenceInput
          mainArea={props.mainArea}
          validatorKey="reference"
          value={freeFieldDefinition.reference}
          onChange={(reference) => { setFreeFieldDefinition({ ...freeFieldDefinition, reference }); }}
        />
      }, {
        key: "visibility",
        label: RESX.GeneralLabels.Visible,
        supportArticleId: serverReference.supportArticleIds.configureFreeFieldsForMobileApp,
        renderEditNew: () => <div className="flex-column gap-small">
          <Checkbox
            checked={freeFieldDefinition.hiddenFromItinerary}
            label={RESX.FreeField.HideFromItinerary}
            onChange={(_, hiddenFromItinerary) => setFreeFieldDefinition({ ...freeFieldDefinition, hiddenFromItinerary })}
          />
        </div>
      }, {
        key: "defaultNumberWithDecimal",
        label: RESX.FreeField.DefaultValue,
        hidden: freeFieldDefinition.type.value !== "decimal",
        renderEditNew: () => <OptionalNumberInput
          value={freeFieldDefinition.defaultNumberWithDecimal}
          onChange={(defaultNumberWithDecimal) => setFreeFieldDefinition({ ...freeFieldDefinition, defaultNumberWithDecimal })}
          onEnterPressed={defaultNumberWithDecimal => setFreeFieldDefinition({ ...freeFieldDefinition, defaultNumberWithDecimal })}
          decimalPoints={2}
          canBeEmpty
          canBeNegative
          width={140}
        />
      }, {
        key: "defaultNumberWithoutDecimal",
        label: RESX.FreeField.DefaultValue,
        hidden: freeFieldDefinition.type.value !== "integer",
        renderEditNew: () => <OptionalNumberInput
          value={freeFieldDefinition.defaultNumberWithoutDecimal}
          onChange={(defaultNumberWithoutDecimal) => setFreeFieldDefinition({ ...freeFieldDefinition, defaultNumberWithoutDecimal })}
          onEnterPressed={defaultNumberWithoutDecimal => setFreeFieldDefinition({ ...freeFieldDefinition, defaultNumberWithoutDecimal })}
          decimalPoints={0}
          canBeEmpty
          canBeNegative
          width={140}
        />
      }, {
        key: "optionsLabel",
        label: RESX.FreeField.DropdownOptions,
        hideLabel: true,
        hidden: freeFieldDefinition.type.value !== "dropdown",
        renderEditNew: () => <div className="main-section-template-fields-padding">
          <h3>{RESX.FreeField.DropdownOptions}</h3>
        </div>
      }, {
        key: "options",
        label: RESX.Status.Options,
        hidden: freeFieldDefinition.type.value !== "dropdown",
        renderEditNew: () => <DropdownOptionsNew
          options={freeFieldDefinition.dropdownOptions.map(option => option.key)}
          defaultOption={freeFieldDefinition.defaultText}
          onSave={(options, defaultText) => setFreeFieldDefinition({
            ...freeFieldDefinition,
            dropdownOptions: options.map(option => ({ key: option, value: option })),
            defaultText
          })}
        />
      }, {
        key: "defaultTextSingleLine",
        label: RESX.FreeField.DefaultValue,
        hidden: freeFieldDefinition.type.value !== "textSingleLine",
        renderEditNew: () => <OptionalTextInput
          value={freeFieldDefinition.defaultText}
          onChange={(defaultText) => setFreeFieldDefinition({ ...freeFieldDefinition, defaultText })}
          maxLength={serverReference.constants.freeFieldDefinition.maxDefaultTextgLength}
        />
      }, {
        key: "defaultTextMultiLine",
        label: RESX.FreeField.DefaultValue,
        hidden: freeFieldDefinition.type.value !== "textMultiLine",
        renderEditNew: () => <OptionalTextAreaInput
          value={freeFieldDefinition.defaultText}
          onChange={(defaultText) => setFreeFieldDefinition({ ...freeFieldDefinition, defaultText })}
          maxLength={serverReference.constants.freeFieldDefinition.maxDefaultTextgLength}
        />
      }, {
        key: "defaultPercentage",
        label: RESX.FreeField.DefaultValue,
        hidden: freeFieldDefinition.type.value !== "percentage",
        renderEditNew: () => <PercentageInput
          canBeNegative={false}
          digitsAfterDecimalPoint={2}
          enablePercentageLabel
          value={freeFieldDefinition.defaultPercentage || 0}
          onChange={(defaultPercentage) => setFreeFieldDefinition({ ...freeFieldDefinition, defaultPercentage: defaultPercentage })}
        />
      }]
    }]}
  />
}